// Componente Textarea reutilizable para la aplicación Amarsi Control Panel
// Proporciona campos de texto multilínea con validación y estados

import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

/**
 * Componente Textarea
 * 
 * Campo de texto multilínea reutilizable con soporte para:
 * - Estados de validación
 * - Placeholder personalizado
 * - Tamaños ajustables
 * - Modo claro/oscuro
 */
const Textarea = forwardRef(({
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  required = false,
  rows = 3,
  variant = 'default',
  size = 'medium',
  className = '',
  helperText,
  label,
  ...props
}, ref) => {
  // Define las clases base según la variante
  const variantClasses = {
    default: 'border-zinc-300 focus:border-blue-500 focus:ring-blue-500 dark:border-zinc-600 dark:focus:border-blue-400 dark:focus:ring-blue-400',
    error: 'border-red-300 focus:border-red-500 focus:ring-red-500 dark:border-red-400 dark:focus:border-red-400 dark:focus:ring-red-400',
    success: 'border-green-300 focus:border-green-500 focus:ring-green-500 dark:border-green-400 dark:focus:border-green-400 dark:focus:ring-green-400',
  };

  // Define las clases de tamaño
  const sizeClasses = {
    small: 'py-1.5 px-3 text-sm',
    medium: 'py-2 px-3 text-base',
    large: 'py-3 px-4 text-lg',
  };

  // Determina la variante basada en el error
  const currentVariant = error ? 'error' : variant;

  // Clases para el textarea
  const textareaClasses = cn(
    'w-full border rounded-md placeholder-zinc-400 dark:placeholder-zinc-500 resize-none',
    'focus:outline-none focus:ring-2 transition-colors duration-200',
    'disabled:bg-neutral-50 disabled:text-zinc-400 disabled:cursor-not-allowed dark:bg-neutral-700/80 dark:text-white dark:disabled:bg-neutral-600 dark:disabled:text-zinc-400',
    variantClasses[currentVariant],
    sizeClasses[size],
    className
  );

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={props.id} className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1 text-left">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <textarea
        ref={ref}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        rows={rows}
        className={textareaClasses}
        placeholder={placeholder}
        {...props}
      />

      {/* Mensaje de error */}
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {/* Texto de ayuda */}
      {helperText && !error && (
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          {helperText}
        </p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

export default Textarea;
